// Faixa de áudio da aba Corte: decodifica o áudio do cut.mp4 UMA vez com WebAudio,
// guarda picos min/max a 100/s e desenha só a janela visível (drawWave do edvid).
// Sem CORS (URL assinada de outro domínio sem cabeçalho) ou sem decodificador, cai
// para blocos por take — a faixa nunca some.
import { useEffect, useRef, useState } from 'react';
import { useTimeline, useViewportCanvas } from './TimelineShell';

export interface Peaks { perSec: number; min: Float32Array; max: Float32Array; duration: number }
type PeakState = { status: 'loading' } | { status: 'ready'; peaks: Peaks } | { status: 'failed'; reason: string };

const cache = new Map<string, Promise<Peaks>>();
const PEAKS_PER_SEC = 100;

async function decodePeaks(url: string): Promise<Peaks> {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  const Ctor: typeof AudioContext | undefined = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) throw new Error('sem WebAudio');
  const ac = new Ctor();
  try {
    const audio = await ac.decodeAudioData(buf);
    const ch = audio.numberOfChannels;
    const data = Array.from({ length: ch }, (_, i) => audio.getChannelData(i));
    const block = Math.max(1, Math.floor(audio.sampleRate / PEAKS_PER_SEC));
    const n = Math.ceil(audio.length / block);
    const min = new Float32Array(n);
    const max = new Float32Array(n);
    for (let b = 0; b < n; b++) {
      let lo = 0; let hi = 0;
      const s0 = b * block;
      const s1 = Math.min(audio.length, s0 + block);
      for (let s = s0; s < s1; s += 2) {
        let v = 0;
        for (let c = 0; c < ch; c++) v += data[c][s];
        v /= ch;
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
      min[b] = lo; max[b] = hi;
    }
    // normaliza pelo pico real para a onda ocupar a faixa
    let peak = 0.0001;
    for (let b = 0; b < n; b++) peak = Math.max(peak, max[b], -min[b]);
    for (let b = 0; b < n; b++) { min[b] /= peak; max[b] /= peak; }
    return { perSec: audio.sampleRate / block, min, max, duration: audio.duration };
  } finally {
    void ac.close().catch(() => undefined);
  }
}

export function useAudioPeaks(url: string | undefined): PeakState {
  const [state, setState] = useState<PeakState>({ status: 'loading' });
  useEffect(() => {
    if (!url) { setState({ status: 'failed', reason: 'sem URL' }); return; }
    let alive = true;
    setState({ status: 'loading' });
    let p = cache.get(url);
    if (!p) {
      p = decodePeaks(url);
      cache.set(url, p);
      p.catch(() => cache.delete(url));
    }
    p.then(peaks => { if (alive) setState({ status: 'ready', peaks }); })
      .catch((e: any) => { if (alive) setState({ status: 'failed', reason: e?.message || 'falha' }); });
    return () => { alive = false; };
  }, [url]);
  return state;
}

/** Canvas da onda. `toMedia` converte tempo da timeline em tempo do arquivo (rascunho → render). */
export function WaveformCanvas({ peaks, toMedia, total, color = 'rgba(163,190,60,0.8)', version }: {
  peaks: Peaks;
  toMedia: (t: number) => number | null;
  total: number;
  color?: string;
  version: unknown;
}) {
  const { pps } = useTimeline();
  const cv = useRef<HTMLCanvasElement>(null);
  useViewportCanvas(cv, (ctx, w, h, x0) => {
    const mid = h / 2;
    ctx.strokeStyle = 'rgba(163,190,60,0.10)';
    ctx.beginPath(); ctx.moveTo(0, mid); ctx.lineTo(w, mid); ctx.stroke();
    ctx.fillStyle = color;
    for (let px = 0; px < w; px++) {
      const tDraft = (x0 + px) / pps;
      if (tDraft > total) break;
      // cada pixel cobre 1/pps s: pega o pico do intervalo, não só uma amostra
      const m0 = toMedia(tDraft);
      if (m0 == null) {
        // trecho estendido ainda não renderizado: tracejado no meio, sem onda inventada
        if (px % 6 < 3) { ctx.fillStyle = 'rgba(251,191,36,0.55)'; ctx.fillRect(px, mid - 0.5, 1, 1); ctx.fillStyle = color; }
        continue;
      }
      const m1 = toMedia(tDraft + 1 / pps) ?? m0 + 1 / pps;
      const a = Math.floor(m0 * peaks.perSec);
      const b = Math.max(a + 1, Math.floor(m1 * peaks.perSec));
      let hi = 0; let lo = 0;
      for (let i = Math.max(0, a); i < Math.min(b, peaks.max.length, a + 400); i++) {
        if (peaks.max[i] > hi) hi = peaks.max[i];
        if (peaks.min[i] < lo) lo = peaks.min[i];
      }
      if (a < 0 || a >= peaks.max.length) continue;
      const top = hi * (mid - 2);
      const bot = lo * (mid - 2);
      ctx.fillRect(px, mid - top, 1, Math.max(1, top - bot));
    }
  }, [pps, peaks, total, version]);
  return <canvas ref={cv} className="pointer-events-none absolute top-0" />;
}
